<template>
  <div class="file-upload-page">
    <!-- 顶部导航条 -->
    <div class="page-header">
      <div class="left">
        <i class="el-icon-back back-icon" @click="goBack"></i>
        <i class="el-icon-document header-icon"></i>
        <span class="title">报告上传与分配</span>
      </div>
      <div class="right">
        <el-button @click="goBack">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting" :disabled="matchedCount === 0">
          确认下发 ({{ matchedCount }})
        </el-button>
      </div>
    </div>

    <!-- 波次选择 -->
    <div class="info-row">
      <div class="info-field">
        <label>选择波次</label>
        <el-select v-model="selectedWaveId" placeholder="选择波次" style="width: 100%" @change="onWaveChange" filterable>
          <el-option v-for="w in waves" :key="w.id || w.Id" :label="w.name || w.Name" :value="w.id || w.Id" />
        </el-select>
      </div>
      <div class="info-field">
        <label>经销商数量</label>
        <div class="info-value">{{ dealers.length }} 个经销商</div>
      </div>
      <div class="info-field">
        <label>已匹配文件</label>
        <div class="info-value">{{ matchedCount }} 个文件 → {{ matchedDealerCount }} 个门店</div>
      </div>
    </div>

    <!-- 主体工作区 -->
    <div class="workspace">
      <!-- 左侧：经销商列表 -->
      <div class="panel left-panel">
        <div class="panel-header">
          <div class="left">
            <i class="el-icon-office-building" style="color: #3b82f6;"></i>
            <span class="title">经销商列表</span>
            <el-tag size="mini" type="success" effect="plain" v-if="matchedDealerCount">{{ matchedDealerCount }} 已匹配</el-tag>
          </div>
          <div class="right">
            <el-input v-model="dealerSearch" placeholder="搜索门店..." prefix-icon="el-icon-search" size="mini" style="width: 160px" clearable />
          </div>
        </div>
        <div class="panel-body">
          <div v-if="!selectedWaveId" class="empty-pool">
            <div class="empty-icon"><i class="el-icon-folder-opened"></i></div>
            <h4>请先选择波次</h4>
            <p>选择波次后将自动加载经销商列表</p>
          </div>
          <div v-else-if="filteredDealers.length === 0" class="empty-pool">
            <div class="empty-icon"><i class="el-icon-warning-outline"></i></div>
            <h4>无匹配经销商</h4>
          </div>
          <div v-else class="dealer-list">
            <div
              v-for="d in filteredDealers" :key="d.dealerCode"
              class="dealer-row"
              :class="{ 'has-files': getDealerFiles(d.dealerCode).length > 0, 'drop-active': activeDrop === d.dealerCode }"
              @dragover.prevent="activeDrop = d.dealerCode"
              @dragleave="activeDrop = null"
              @drop.prevent="handleDropOnDealer($event, d)"
            >
              <div class="dr-main">
                <div class="dr-info">
                  <div class="dr-name">{{ d.dealerName }}</div>
                  <div class="dr-code">{{ d.dealerCode }}</div>
                </div>
                <el-tag v-if="getDealerFiles(d.dealerCode).length" size="mini" type="success">
                  {{ getDealerFiles(d.dealerCode).length }} 文件
                </el-tag>
              </div>
              <div v-if="getDealerFiles(d.dealerCode).length" class="dr-files">
                <div v-for="(f, idx) in getDealerFiles(d.dealerCode)" :key="f.uid" class="file-chip">
                  <i :class="getFileIcon(f.name)" style="font-size: 12px;"></i>
                  <span :title="f.name">{{ f.name }}</span>
                  <i class="el-icon-close" @click.stop="removeFileFromDealer(d.dealerCode, idx)"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧：文件池 -->
      <div class="panel right-panel">
        <div class="panel-header">
          <div class="left">
            <i class="el-icon-files" style="color: #3b82f6;"></i>
            <span class="title">报告文件池</span>
          </div>
          <div class="right">
            <span class="toggle-label">仅显示未分配</span>
            <el-switch v-model="showUnassignedOnly" active-color="#3b82f6"></el-switch>
          </div>
        </div>
        <div class="panel-toolbar">
          <el-button size="small" type="primary" round @click="smartMatch" :disabled="filePool.length === 0">
            <i class="el-icon-connection"></i> 智能匹配
          </el-button>
          <el-upload action="" :http-request="handleUploadFile" :show-file-list="false" multiple style="display: inline-block;">
            <el-button size="small" round plain><i class="el-icon-upload2"></i> 上传文件</el-button>
          </el-upload>
          <el-button size="small" round plain @click="triggerFolderUpload">
            <i class="el-icon-folder-opened"></i> 上传文件夹
          </el-button>
          <input type="file" ref="folderInput" webkitdirectory style="display: none" @change="handleFolderSelect" />
        </div>

        <div
          class="panel-body"
          v-loading="uploading"
          element-loading-text="正在上传中..."
          @dragover.prevent="handlePoolDragOver"
          @dragleave="handlePoolDragLeave"
          @drop.prevent="handlePoolDrop"
        >
          <div v-if="visibleFiles.length === 0 && !uploading" class="empty-pool">
            <div class="empty-icon"><i class="el-icon-upload"></i></div>
            <h4>准备上传报告文件</h4>
            <p>拖拽文件或文件夹到此处，或者点击上<br>方按钮上传</p>
            <div class="file-types">
              <span><i class="el-icon-document"></i> PDF</span>
              <span><i class="el-icon-document-copy"></i> Excel / Word</span>
              <span><i class="el-icon-picture-outline"></i> 图片</span>
            </div>
          </div>

          <div class="file-grid">
            <div
              v-for="file in visibleFiles" :key="file.uid"
              class="pool-file-item"
              draggable="true"
              @dragstart="handleDragStart($event, file)"
              :class="{ 'is-assigned': file.assignedTo }"
            >
              <div class="file-content">
                <div class="file-icon-large" :class="getFileIconClass(file.name)">
                  <i :class="getFileIcon(file.name)"></i>
                </div>
                <div class="status-badge" v-if="file.assignedTo"><i class="el-icon-check"></i></div>
              </div>
              <div class="file-name" :title="file.name">{{ file.name }}</div>
            </div>
          </div>

          <div v-if="isPoolDropping" class="drop-mask">
            <i class="el-icon-upload"></i>
            <p>释放以上传文件到文件池</p>
          </div>
        </div>

        <div class="panel-footer">
          <span>支持批量拖拽多级文件夹</span>
          <span>文件上限: 500MB</span>
        </div>
      </div>
    </div>

    <!-- 匹配结果弹窗 -->
    <el-dialog title="智能匹配结果" :visible.sync="showMatchResult" width="550px" custom-class="match-result-dialog">
      <p>匹配成功：<span style="color: #67C23A; font-weight: 600;">{{ matchResult.success }}</span> 个文件</p>
      <p>未匹配：<span style="color: #F56C6C; font-weight: 600;">{{ matchResult.failed }}</span> 个文件</p>
      <el-table :data="matchResult.list" height="280" border size="mini">
        <el-table-column prop="name" label="文件名" show-overflow-tooltip></el-table-column>
        <el-table-column prop="status" label="状态" width="90">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 'success' ? 'success' : 'info'" size="mini">{{ row.status === 'success' ? '匹配' : '未匹配' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="target" label="匹配门店" show-overflow-tooltip></el-table-column>
      </el-table>
      <span slot="footer"><el-button type="primary" @click="showMatchResult = false">确定</el-button></span>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'index/axios'
import { smartUpload } from '@/utils/ossUpload'

const uuid = () => 'xxxxxxxx-xxxx-4xxx'.replace(/[xy]/g, c => {
  const r = Math.random() * 16 | 0
  return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
})

export default {
  name: 'FileDistUpload',
  data() {
    return {
      projectId: null,
      waves: [],
      selectedWaveId: '',
      dealers: [],
      dealerSearch: '',
      filePool: [],
      showUnassignedOnly: false,
      uploading: false,
      isPoolDropping: false,
      draggedFile: null,
      activeDrop: null,
      assignedMap: {},
      submitting: false,
      showMatchResult: false,
      matchResult: { success: 0, failed: 0, list: [] }
    }
  },
  computed: {
    filteredDealers() {
      if (!this.dealerSearch) return this.dealers
      const kw = this.dealerSearch.toLowerCase()
      return this.dealers.filter(d => (d.dealerName || '').toLowerCase().includes(kw) || (d.dealerCode || '').toLowerCase().includes(kw))
    },
    visibleFiles() {
      return this.showUnassignedOnly ? this.filePool.filter(f => !f.assignedTo) : this.filePool
    },
    matchedCount() { return this.filePool.filter(f => f.assignedTo).length },
    matchedDealerCount() {
      return Object.keys(this.assignedMap).filter(k => this.assignedMap[k] && this.assignedMap[k].length > 0).length
    }
  },
  mounted() {
    this.projectId = this.$route.query.projectId || this.$route.params.projectId
    this.loadWaves()
  },
  methods: {
    goBack() { this.$router.back() },

    // ===== 波次与经销商 =====
    async loadWaves() {
      try {
        const res = await axios.post('/WaveManager/wave/list', { ProjectId: this.projectId, Page: 1, PageSize: 50 })
        const data = res.data?.data || res.data || {}
        this.waves = data.rows || data.Rows || data.records || []
        const active = this.waves.find(w => (w.status || w.Status) === 1)
        if (active) { this.selectedWaveId = active.id || active.Id; this.onWaveChange() }
      } catch (e) { console.error('[FileDistUpload] loadWaves', e) }
    },

    onWaveChange() {
      const wave = this.waves.find(w => (w.id || w.Id) === this.selectedWaveId)
      if (!wave) { this.dealers = []; return }
      const dealers = wave.dealers || wave.Dealers || []
      this.dealers = dealers.map(d => ({
        dealerCode: d.dealerCode || d.DealerCode || d.code || '',
        dealerName: d.dealerName || d.DealerName || d.name || ''
      }))
      if (this.dealers.length === 0) this.loadDealersFromWave()
      this.assignedMap = {}
      this.dealers.forEach(d => { this.$set(this.assignedMap, d.dealerCode, []) })
    },

    async loadDealersFromWave() {
      try {
        const res = await axios.post('/WaveManager/wave/dealer/list', { WaveId: this.selectedWaveId, Page: 1, PageSize: 500 })
        const data = res.data?.data || res.data || {}
        const list = data.rows || data.Rows || []
        this.dealers = list.map(d => ({ dealerCode: d.dealerCode || d.DealerCode || '', dealerName: d.dealerName || d.DealerName || '' }))
        this.assignedMap = {}
        this.dealers.forEach(d => { this.$set(this.assignedMap, d.dealerCode, []) })
      } catch (e) { console.error('[FileDistUpload] loadDealersFromWave', e) }
    },

    getDealerFiles(code) { return this.assignedMap[code] || [] },

    removeFileFromDealer(code, idx) {
      const list = this.assignedMap[code]; if (!list) return
      const removed = list.splice(idx, 1)[0]
      if (removed) { const f = this.filePool.find(p => p.uid === removed.uid); if (f) f.assignedTo = null }
    },

    // ===== 拖拽 =====
    handleDragStart(e, file) { this.draggedFile = file; e.dataTransfer.effectAllowed = 'copyMove' },

    handleDropOnDealer(e, dealer) {
      this.activeDrop = null
      if (this.draggedFile) { this.assignFileToDealer(this.draggedFile, dealer.dealerCode); this.draggedFile = null }
    },

    assignFileToDealer(file, dealerCode) {
      if (file.assignedTo) {
        const old = this.assignedMap[file.assignedTo]
        if (old) { const i = old.findIndex(f => f.uid === file.uid); if (i > -1) old.splice(i, 1) }
      }
      const list = this.assignedMap[dealerCode] || []
      if (!list.some(f => f.uid === file.uid)) { list.push(file); this.$set(this.assignedMap, dealerCode, list) }
      file.assignedTo = dealerCode
    },

    // ===== 上传 =====
    triggerFolderUpload() { this.$refs.folderInput.click() },
    handleFolderSelect(e) {
      const files = Array.from(e.target.files).map(f => { f.relativePath = f.webkitRelativePath || f.name; return f })
      if (files.length) this.batchUpload(files)
      e.target.value = ''
    },
    handleUploadFile(param) { const f = param.file; f.relativePath = f.name; this.batchUpload([f]) },

    handlePoolDragOver(e) { e.dataTransfer.dropEffect = 'copy'; this.isPoolDropping = true },
    handlePoolDragLeave() { this.isPoolDropping = false },

    async handlePoolDrop(e) {
      this.isPoolDropping = false
      const items = e.dataTransfer.items; if (!items) return
      const fileList = []; const promises = []
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry ? items[i].webkitGetAsEntry() : null
        if (entry) promises.push(this.traverseFileTree(entry, fileList, ''))
        else { const f = items[i].getAsFile(); if (f) { f.relativePath = f.name; fileList.push(f) } }
      }
      await Promise.all(promises)
      if (fileList.length) this.batchUpload(fileList)
    },

    traverseFileTree(item, fileList, parentPath) {
      return new Promise(resolve => {
        const path = parentPath ? `${parentPath}/${item.name}` : item.name
        if (item.isFile) { item.file(f => { f.relativePath = path; fileList.push(f); resolve() }) }
        else if (item.isDirectory) {
          const reader = item.createReader()
          const read = () => { reader.readEntries(entries => {
            if (!entries.length) { resolve(); return }
            Promise.all(entries.map(e => this.traverseFileTree(e, fileList, path))).then(read)
          }) }
          read()
        } else resolve()
      })
    },

    async batchUpload(files) {
      this.uploading = true
      let ok = 0, fail = 0
      const queue = [...files]
      const worker = async () => { while (queue.length) { const f = queue.shift(); try { const r = await smartUpload(f, 'report/files'); this.addToPool(f, r); ok++ } catch (e) { console.error(`上传失败: ${f.name}`, e); fail++ } } }
      await Promise.all(Array.from({ length: 3 }, () => worker()))
      this.uploading = false
      if (fail) this.$message.warning(`成功 ${ok}，失败 ${fail}`)
      else if (ok) this.$message.success(`上传 ${ok} 个文件`)
    },

    addToPool(file, result) {
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase().replace('.', '')
      this.filePool.unshift({ uid: uuid(), name: file.name, relativePath: file.relativePath || file.name, url: result.url, size: file.size, fileType: ext, assignedTo: null })
    },

    // ===== 智能匹配 =====
    smartMatch() {
      const result = { success: 0, failed: 0, list: [] }
      this.filePool.forEach(file => {
        if (file.assignedTo) return
        const target = file.relativePath || file.name
        for (const d of this.dealers) {
          if (target.includes(d.dealerCode)) {
            this.assignFileToDealer(file, d.dealerCode)
            result.success++
            result.list.push({ name: file.name, status: 'success', target: `${d.dealerName} (${d.dealerCode})` })
            return
          }
        }
        result.failed++
        result.list.push({ name: file.name, status: 'failed', target: '' })
      })
      this.matchResult = result
      this.showMatchResult = true
    },

    // ===== 提交 =====
    async handleSubmit() {
      const files = []
      for (const [code, list] of Object.entries(this.assignedMap)) {
        if (!list || !list.length) continue
        const dealer = this.dealers.find(d => d.dealerCode === code)
        list.forEach(f => { files.push({ StoreCode: code, StoreName: dealer ? dealer.dealerName : '', FileName: f.name, FileUrl: f.url, FileType: f.fileType, FileSize: f.size || 0 }) })
      }
      if (!files.length) { this.$message.warning('请先分配文件到门店'); return }
      this.submitting = true
      try {
        await axios.post('/StoreFile/batch/save', { ProjectId: this.projectId, WaveId: this.selectedWaveId, Files: files })
        this.$message.success(`成功下发 ${files.length} 个文件`)
        this.$router.back()
      } catch (e) { this.$message.error('下发失败：' + (e.message || '未知错误')) }
      finally { this.submitting = false }
    },

    // ===== 工具 =====
    getFileIcon(name) {
      if (!name) return 'el-icon-document'
      const ext = name.split('.').pop().toLowerCase()
      if (ext === 'pdf') return 'el-icon-document'
      if (['xls', 'xlsx'].includes(ext)) return 'el-icon-document-copy'
      if (['doc', 'docx'].includes(ext)) return 'el-icon-document-checked'
      if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'el-icon-picture-outline'
      return 'el-icon-document'
    },
    getFileIconClass(name) {
      if (!name) return 'file-default'
      const ext = name.split('.').pop().toLowerCase()
      if (ext === 'pdf') return 'file-pdf'
      if (['xls', 'xlsx'].includes(ext)) return 'file-excel'
      if (['doc', 'docx'].includes(ext)) return 'file-word'
      if (['jpg', 'jpeg', 'png'].includes(ext)) return 'file-image'
      return 'file-default'
    }
  }
}
</script>

<style scoped>
.file-upload-page { min-height: 100vh; background: #f5f7fa; }

/* 顶部导航 - 跟申诉下发一致 */
.page-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 14px 28px; background: #fff; border-bottom: 1px solid #e5e7eb;
  position: sticky; top: 0; z-index: 100;
}
.page-header .left { display: flex; align-items: center; gap: 12px; }
.back-icon { font-size: 20px; color: #6b7280; cursor: pointer; transition: color 0.15s; }
.back-icon:hover { color: #111827; }
.header-icon { font-size: 20px; color: #3b82f6; }
.page-header .title { font-size: 16px; font-weight: 600; color: #1f2937; }
.page-header .right { display: flex; gap: 10px; align-items: center; }
.page-header .right .el-button { border-radius: 20px; padding: 10px 24px; }
.page-header .right .el-button--primary { background-color: #1d4ed8 !important; border-color: #1d4ed8 !important; color: #fff !important; }
.page-header .right .el-button--default { color: #374151 !important; border-color: #d1d5db !important; background: #fff !important; }
.page-header .right .el-button--default:hover { color: #3b82f6 !important; border-color: #3b82f6 !important; }

/* 信息行 */
.info-row {
  display: flex; gap: 24px; padding: 16px 28px; background: #fff;
  border-bottom: 1px solid #f3f4f6;
}
.info-field { flex: 1; }
.info-field label { display: block; font-size: 12px; color: #9ca3af; margin-bottom: 6px; font-weight: 500; }
.info-value { font-size: 14px; color: #374151; font-weight: 500; padding: 8px 0; }

/* 工作区 */
.workspace { display: flex; gap: 20px; padding: 20px 28px; height: calc(100vh - 185px); }

/* 面板通用 */
.panel { background: #fff; border-radius: 14px; border: 1px solid #e5e7eb; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
.left-panel { flex: 4; min-width: 0; }
.right-panel { flex: 6; min-width: 0; }
.panel-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 18px; border-bottom: 1px solid #f3f4f6; flex-shrink: 0; }
.panel-header .left { display: flex; align-items: center; gap: 8px; }
.panel-header .title { font-size: 15px; font-weight: 600; color: #1f2937; }
.panel-header .right { display: flex; align-items: center; gap: 8px; }
.panel-toolbar { display: flex; align-items: center; gap: 10px; padding: 12px 18px; border-bottom: 1px solid #f3f4f6; flex-shrink: 0; }
.panel-toolbar .el-button { border-radius: 20px !important; padding: 8px 18px; font-size: 13px; }
.panel-toolbar .el-button--primary { background-color: #3b82f6 !important; border-color: #3b82f6 !important; }
.panel-toolbar .el-button--default { color: #374151 !important; border-color: #d1d5db !important; background: #fff !important; }
.panel-toolbar .el-button--default:hover { color: #3b82f6 !important; border-color: #3b82f6 !important; }
.panel-body { flex: 1; overflow-y: auto; padding: 12px 16px; position: relative; }
.panel-footer { padding: 10px 18px; border-top: 1px solid #f3f4f6; font-size: 12px; color: #9ca3af; display: flex; justify-content: space-between; flex-shrink: 0; }
.toggle-label { font-size: 12px; color: #9ca3af; }

/* 空状态 */
.empty-pool { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; text-align: center; color: #9ca3af; }
.empty-pool .empty-icon { font-size: 40px; color: #d1d5db; margin-bottom: 12px; }
.empty-pool h4 { margin: 0 0 6px; font-size: 15px; color: #6b7280; }
.empty-pool p { margin: 0; font-size: 13px; line-height: 1.6; }
.file-types { display: flex; gap: 14px; margin-top: 16px; }
.file-types span { font-size: 12px; color: #9ca3af; display: flex; align-items: center; gap: 4px; }

/* 经销商列表 */
.dealer-list { display: flex; flex-direction: column; gap: 4px; }
.dealer-row { padding: 8px 12px; border: 1px solid #f0f0f0; border-radius: 8px; transition: all 0.15s; }
.dealer-row:hover { border-color: #d1d5db; }
.dealer-row.has-files { border-color: #86efac; background: #f0fdf4; }
.dealer-row.drop-active { border-color: #3b82f6; background: #eff6ff; box-shadow: 0 0 0 2px rgba(59,130,246,0.2); }
.dr-main { display: flex; justify-content: space-between; align-items: center; }
.dr-name { font-size: 13px; font-weight: 600; color: #111827; }
.dr-code { font-size: 11px; color: #9ca3af; }
.dr-files { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px; }
.file-chip { display: inline-flex; align-items: center; gap: 3px; padding: 2px 7px; background: #fff; border: 1px solid #e5e7eb; border-radius: 5px; font-size: 11px; color: #374151; max-width: 180px; }
.file-chip span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.file-chip .el-icon-close { cursor: pointer; color: #d1d5db; font-size: 12px; flex-shrink: 0; }
.file-chip .el-icon-close:hover { color: #ef4444; }

/* 文件网格 */
.file-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(100px, 1fr)); gap: 10px; }
.pool-file-item { cursor: grab; transition: transform 0.15s; }
.pool-file-item:active { cursor: grabbing; }
.pool-file-item:hover { transform: translateY(-2px); }
.pool-file-item.is-assigned { opacity: 0.45; }
.file-content { position: relative; border-radius: 10px; overflow: hidden; aspect-ratio: 1; }
.file-icon-large { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 28px; border-radius: 10px; }
.file-pdf { background: #fee2e2; color: #dc2626; }
.file-excel { background: #dcfce7; color: #16a34a; }
.file-word { background: #dbeafe; color: #2563eb; }
.file-image { background: #fef3c7; color: #d97706; }
.file-default { background: #f3f4f6; color: #6b7280; }
.status-badge { position: absolute; top: 4px; right: 4px; width: 20px; height: 20px; border-radius: 50%; background: #22c55e; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 11px; }
.pool-file-item .file-name { font-size: 11px; color: #6b7280; margin-top: 4px; text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* 拖入遮罩 */
.drop-mask { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(59,130,246,0.08); border: 2px dashed #3b82f6; border-radius: 10px; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #3b82f6; font-size: 14px; z-index: 10; }
.drop-mask i { font-size: 36px; margin-bottom: 8px; }
</style>

<style>
/* 匹配结果弹窗全局样式 */
.match-result-dialog { border-radius: 14px !important; overflow: hidden; }
.match-result-dialog .el-dialog__header { padding: 20px 24px 12px; }
.match-result-dialog .el-dialog__title { font-size: 17px; font-weight: 600; color: #1f2937; }
.match-result-dialog .el-dialog__body { padding: 0 24px 20px; }
.match-result-dialog .el-dialog__body p { font-size: 14px; color: #374151; margin: 8px 0; }
.match-result-dialog .el-table { border-radius: 8px !important; overflow: hidden; }
.match-result-dialog .el-table th { background: #f9fafb !important; color: #6b7280; font-weight: 500; font-size: 13px; }
.match-result-dialog .el-table td { font-size: 13px; color: #374151; }
.match-result-dialog .el-dialog__footer { padding: 0 24px 20px; }
.match-result-dialog .el-dialog__footer .el-button { border-radius: 20px; padding: 10px 28px; }
.match-result-dialog .el-dialog__footer .el-button--primary { background-color: #1d4ed8; border-color: #1d4ed8; }
</style>
